import { useState, useMemo } from "react";
import { ADVICE, STAGES, AGE_GROUPS } from "@/data/parentingData";
import { Clock, Search, X } from "lucide-react";

const AGE_LABEL = {
  "0-2": "Bébé · 0-2 ans",
  "3-5": "Petite enfance · 3-5 ans",
  "6-9": "Enfance · 6-9 ans",
  "10-12": "Préado · 10-12 ans",
  "13+": "Ado · 13 ans et +",
};

const badgeColor = {
  "0-2": "pastel-blush",
  "3-5": "pastel-butter",
  "6-9": "pastel-sage",
  "10-12": "pastel-sky",
  "13+": "pastel-lavender",
};

export default function AdvicePage() {
  const [ageTab, setAgeTab] = useState("3-5");
  const [stage, setStage] = useState("Tous");
  const [category, setCategory] = useState("Tous");
  const [query, setQuery] = useState("");

  const byAge = useMemo(() => ADVICE.filter((a) => a.age === ageTab), [ageTab]);

  const stages = useMemo(() => {
    const present = new Set(byAge.map((a) => a.stage));
    const ordered = (STAGES[ageTab] || []).filter((s) => present.has(s));
    return ["Tous", ...ordered];
  }, [byAge, ageTab]);

  const categories = useMemo(() => {
    const pool = stage === "Tous" ? byAge : byAge.filter((a) => a.stage === stage);
    return ["Tous", ...Array.from(new Set(pool.map((a) => a.category))).sort((a, b) => a.localeCompare(b, "fr"))];
  }, [byAge, stage]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return byAge.filter(
      (a) =>
        (stage === "Tous" || a.stage === stage) &&
        (category === "Tous" || a.category === category) &&
        (!q ||
          a.title.toLowerCase().includes(q) ||
          a.body.toLowerCase().includes(q) ||
          a.category.toLowerCase().includes(q) ||
          a.stage.toLowerCase().includes(q))
    );
  }, [byAge, stage, category, query]);

  const resetAge = (g) => {
    setAgeTab(g);
    setStage("Tous");
    setCategory("Tous");
  };

  return (
    <div className="space-y-6" data-testid="advice-hub-section">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Conseils par âge
        </h1>
        <p className="mt-2 text-[#77849C]">
          {ADVICE.length} conseils concrets, triés par étape de développement — parce qu'un bébé de 3 mois et un enfant de 2 ans n'ont pas les mêmes besoins.
        </p>
      </div>

      <div className="filter-row no-scrollbar" data-testid="advice-age-tabs">
        {AGE_GROUPS.map((g) => (
          <button
            key={g}
            data-testid={`age-tab-${g}`}
            onClick={() => resetAge(g)}
            className={`rounded-full px-5 py-2 text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
              ageTab === g ? "bg-[#23375E] text-white" : `${badgeColor[g]} text-[#16233D] hover:brightness-95`
            }`}
          >
            {AGE_LABEL[g]}
          </button>
        ))}
      </div>

      {stages.length > 2 && (
        <div className="filter-row items-center" data-testid="advice-stage-filters">
          <span className="text-xs uppercase tracking-wide text-[#99A5BB] mr-1">Étape</span>
          {stages.map((s) => (
            <button
              key={s}
              data-testid={`stage-${s}`}
              onClick={() => {
                setStage(s);
                setCategory("Tous");
              }}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors duration-200 ${
                stage === s
                  ? "bg-[#23375E] text-white"
                  : "bg-white border border-[#E3E8F1] text-[#44506B] hover:border-[#23375E]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="filter-row items-center" data-testid="advice-category-filters">
        <span className="text-xs uppercase tracking-wide text-[#99A5BB] mr-1">Thème</span>
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`cat-${c}`}
            onClick={() => setCategory(c)}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors duration-200 ${
              category === c
                ? "bg-[#16233D] text-white"
                : "bg-white border border-[#E3E8F1] text-[#44506B] hover:border-[#23375E]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#99A5BB]" />
          <input
            data-testid="advice-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher : sommeil, colère, écrans…"
            className="w-full rounded-full border border-[#E3E8F1] bg-white pl-9 pr-9 py-2 text-sm text-[#16233D] outline-none focus:border-[#23375E] transition-colors"
          />
          {query && (
            <button
              data-testid="advice-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#99A5BB] hover:text-[#16233D]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <span className="text-sm text-[#77849C]" data-testid="advice-result-count">
          {filtered.length} conseil{filtered.length > 1 ? "s" : ""}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="soft-card p-8 text-center text-[#77849C]" data-testid="advice-empty-state">
          Aucun conseil ne correspond à cette recherche. Essayez un autre mot ou un autre thème.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((a) => (
            <article key={a.id} data-testid={`advice-${a.id}`} className="soft-card p-6 flex flex-col">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`age-badge ${badgeColor[a.age]}`}>{a.stage}</span>
                <span className="text-xs text-[#77849C]">{a.category}</span>
              </div>
              <h3 className="text-lg font-semibold text-[#16233D]">{a.title}</h3>
              <p className="mt-3 text-[#44506B] leading-relaxed">{a.body}</p>
              <div className="mt-4 flex items-center gap-1 text-xs text-[#77849C]">
                <Clock className="h-3 w-3" /> {a.duration}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
